import React, { useState, useEffect } from 'react';
import API from '../../services/api';
import { THEME } from '../../constants/theme';

const ComplaintDetail = ({ id, onBack }) => {
    const [detail, setDetail] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    
    const [updateData, setUpdateData] = useState({
        status: '',
        assigned_to: '',
        followup_comment: ''
    });

    useEffect(() => {
        if (id) fetchData();
    }, [id]);

    // Employee Search Logic
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm.length >= 2) {
                searchEmployees(searchTerm);
            } else {
                setEmployees([]);
            }
        }, 500); // 500ms delay to prevent too many API calls

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const fetchData = async () => {
        try {
            const res = await API.get(`/customer-care/complaints/${id}`);
            const complaint = res.data.data.complaint;
            setDetail(res.data.data);
            setUpdateData(prev => ({ 
                ...prev, 
                status: complaint.status,
                assigned_to: complaint.assigned_to || ''
            }));
            // Agar pehle se assigned hai to uska naam dikhane ke liye
            if(complaint.assigned_to_name) setSearchTerm(complaint.assigned_to_name);
        } catch (err) {
            console.error("Error fetching detail:", err);
        }
    };

    const searchEmployees = async (query) => {
        setIsSearching(true);
        try {
            // Aapki provide ki hui API endpoint ka istemal
            const res = await API.get(`/customer-care/employees/search?query=${query}`);
            setEmployees(res.data.data || []);
            setShowResults(true);
        } catch (err) {
            console.error("Error searching employees:", err);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectEmployee = (emp) => {
        setUpdateData({ ...updateData, assigned_to: emp.id });
        setSearchTerm(emp.full_name);
        setShowResults(false);
    };

    const handleUpdate = async () => {
        if (!updateData.status) return alert("Please select a status");
        try {
            await API.put(`/customer-care/complaints/${id}`, updateData);
            alert("Complaint Updated Successfully!");
            fetchData();
        } catch (err) { 
            alert("Error updating complaint"); 
        }
    };

    if (!detail) return <div style={{ padding: '20px' }}>Loading complaint details...</div>;

    return (
        <div style={styles.container}>
            <button onClick={onBack} style={styles.backBtn}>← Back to List</button>
            
            <div style={styles.grid}>
                {/* Information Area */}
                <div style={styles.card}>
                    <div style={styles.header}>
                        <h3 style={{ margin: 0 }}>Ticket: {detail.complaint.ticket_number}</h3>
                        <span style={styles.badge}>{detail.complaint.status}</span>
                    </div>
                    <div style={styles.infoSection}>
                        <p><strong>Customer:</strong> {detail.complaint.customer_name}</p>
                        <p><strong>Phone:</strong> {detail.complaint.customer_phone}</p>
                        <p><strong>Subject:</strong> {detail.complaint.subject}</p>
                        <p><strong>Description:</strong> {detail.complaint.description}</p>
                    </div>
                    
                    <hr style={styles.divider} />
                    
                    <h4>Follow-up History</h4>
                    <div style={styles.historyList}>
                        {detail.followups.map(f => (
                            <div key={f.id} style={styles.historyItem}>
                                <div>
                                    <small style={{ color: '#64748b' }}>{new Date(f.created_at).toLocaleString()}</small>
                                    <p style={{ margin: '5px 0' }}><strong>{f.user_name}:</strong> {f.comment}</p>
                                </div>
                                <span style={styles.miniBadge}>{f.status_change}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions Area */}
                <div style={styles.card}>
                    <h4 style={{ marginTop: 0 }}>Take Action</h4>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Change Status</label>
                        <select 
                            value={updateData.status} 
                            onChange={e => setUpdateData({...updateData, status: e.target.value})}
                            style={styles.input}
                        >
                            <option value="Open">Open</option>
                            <option value="In_Progress">In Progress</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>

                    {/* Employee Search Implementation */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Assign to Employee (Search by Name/CNIC)</label>
                        <div style={{ position: 'relative' }}>
                            <input 
                                type="text"
                                placeholder="Type name or CNIC..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowResults(true);
                                }}
                                style={styles.input}
                            />
                            {isSearching && <small style={styles.searchingText}>Searching...</small>}
                            
                            {showResults && employees.length > 0 && (
                                <div style={styles.searchResults}>
                                    {employees.map(emp => (
                                        <div 
                                            key={emp.id} 
                                            onClick={() => handleSelectEmployee(emp)}
                                            style={styles.searchItem}
                                        >
                                            <div style={{fontWeight: '600'}}>{emp.full_name}</div>
                                            <div style={{fontSize: '11px', color: '#64748b'}}>
                                                {emp.cnic} | {emp.roles || 'No Role'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Add Comment</label>
                        <textarea 
                            placeholder="Internal notes..."
                            value={updateData.followup_comment}
                            onChange={e => setUpdateData({...updateData, followup_comment: e.target.value})}
                            style={{...styles.input, height: '100px', resize: 'none'}}
                        />
                    </div>
                    
                    <button onClick={handleUpdate} style={styles.primaryBtn}>Save Update</button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    // ... aapke purane styles yahan aayenge ...
    container: { maxWidth: '1200px', margin: '0 auto', padding: '10px' },
    backBtn: { padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', marginBottom: '15px', fontWeight: '500' },
    grid: { display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px' },
    card: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    infoSection: { lineHeight: '1.6', color: '#334155' },
    badge: { backgroundColor: '#f1f5f9', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' },
    miniBadge: { backgroundColor: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', height: 'fit-content' },
    divider: { border: '0', borderTop: '1px solid #f1f5f9', margin: '20px 0' },
    historyList: { display: 'flex', flexDirection: 'column', gap: '10px' },
    historyItem: { display: 'flex', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px' },
    formGroup: { marginBottom: '15px' },
    label: { display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#64748b' },
    input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', boxSizing: 'border-box' },
    primaryBtn: { width: '100%', padding: '12px', backgroundColor: THEME.secondary || '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', marginTop: '10px' },
    
    // New Search Styles
    searchResults: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        zIndex: 10,
        maxHeight: '200px',
        overflowY: 'auto'
    },
    searchItem: {
        padding: '10px',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        transition: 'background 0.2s'
    },
    searchingText: {
        position: 'absolute',
        right: '10px',
        top: '10px',
        color: '#94a3b8'
    }
};

export default ComplaintDetail;